import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import i18n from 'i18next';
import { StateCreator } from 'zustand';

import { APPLICANT_TASK_STATUSES } from './constants/applicantTaskStatuses';
import { CANCELED_TASK_STATUSES } from './constants/canceledTaskStatuses';
import { COMPLETED_TASK_STATUSES } from './constants/completedTaskStatuses';
import { FAILED_TASK_STATUSES } from './constants/failedTaskStatuses';
import { HANDOFF_TASK_STATUSES } from './constants/handOffTaskStatuses';
import { QUEUED_TASK_STATUSES } from './constants/queuedTaskStatuses';
import { tasks } from './constants/tasks';
import { VERIFICATION_TASK_STATUSES } from './constants/verificationTaskStatuses';
import { TaskName } from './enums/TaskName';
import { Task } from './types/Task';
import { TaskState } from './types/TaskState';
import { TaskStoreDependencies } from './types/TaskStoreDependencies';

export const taskStore =
  ({ useDashboardStore }: TaskStoreDependencies): StateCreator<TaskState> =>
  (set, get) => ({
    computed: {
      get applicantTask(): Task {
        const {
          computed: { vehicleId },
        } = useDashboardStore.getState();

        const applicantTask = tasks[TaskName.APPLICANT];
        applicantTask.setDestination(`/applicant/${vehicleId}`);

        return applicantTask;
      },
      get canceledTask(): Task {
        return tasks[TaskName.CANCELED];
      },
      get completeTask(): Task {
        return tasks[TaskName.COMPLETE];
      },
      get failedTask(): Task {
        return tasks[TaskName.FAILED];
      },
      get handoffTask(): Task {
        const {
          computed: { vehicleBrand },
        } = useDashboardStore.getState();

        const handoffTask = tasks[TaskName.HANDOFF];
        handoffTask.setDescription(
          i18n.t('tasks.handoff.description', { brand: vehicleBrand }),
        );

        return handoffTask;
      },
      get queuedTask(): Task {
        return tasks[TaskName.QUEUED];
      },
      get verificationsTask(): Task {
        const {
          computed: { vehicleId },
        } = useDashboardStore.getState();

        const verificationsTask = tasks[TaskName.VERIFICATIONS];
        verificationsTask.setDestination(`/verifications/${vehicleId}`);

        return verificationsTask;
      },
    },

    getTaskByApplicationStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): Task => {
      const {
        computed: {
          applicantTask,
          canceledTask,
          completeTask,
          failedTask,
          handoffTask,
          queuedTask,
          verificationsTask,
        },
        isApplicantTaskStatus,
        isVerificationTaskStatus,
        isCompletedTaskStatus,
        isCanceledTaskStatus,
        isFailedTaskStatus,
        isHandOffTaskStatus,
        isQueuedTaskStatus,
      } = get();

      if (!status || isApplicantTaskStatus(status)) {
        return applicantTask;
      }

      if (isVerificationTaskStatus(status)) {
        return verificationsTask;
      }

      if (isCompletedTaskStatus(status)) {
        return completeTask;
      }

      if (isCanceledTaskStatus(status)) {
        return canceledTask;
      }

      if (isFailedTaskStatus(status)) {
        return failedTask;
      }

      if (isHandOffTaskStatus(status)) {
        return handoffTask;
      }

      if (isQueuedTaskStatus(status)) {
        return queuedTask;
      }

      return completeTask;
    },
    isApplicantTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return APPLICANT_TASK_STATUSES.includes(status);
    },
    isCanceledTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return CANCELED_TASK_STATUSES.includes(status);
    },
    isCompletedTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return COMPLETED_TASK_STATUSES.includes(status);
    },
    isFailedTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return FAILED_TASK_STATUSES.includes(status);
    },
    isHandOffTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return HANDOFF_TASK_STATUSES.includes(status);
    },
    isQueuedTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return QUEUED_TASK_STATUSES.includes(status);
    },
    isVerificationTaskStatus: (
      status: ChecklightSessionStatus | string | null | undefined,
    ): boolean => {
      return VERIFICATION_TASK_STATUSES.includes(status);
    },
  });
